import { useCallback, useMemo, useState } from 'react';
import { useLocation } from 'react-router';

import type { FilteredListingSearchRowProps } from './FilteredListingSearchRow';
import type { ListingUrlAdapter } from './useListingUrlState';

export type FilteredListingControlsSnapshot<TFilters> = {
  q: string;
  sort: string;
  page: number;
  filters: TFilters;
};

type UseFilteredListingControlsOptions<TFilters> = {
  defaultFilters: TFilters;
  isFiltersActive: (filters: TFilters) => boolean;
  urlAdapter?: ListingUrlAdapter<FilteredListingControlsSnapshot<TFilters>>;
  debounceMs?: number;
  initialSort?: string;
  initialPage?: number;
  searchPlaceholder: string;
};

function readControlsInitialState<TFilters>(
  urlAdapter:
    ListingUrlAdapter<FilteredListingControlsSnapshot<TFilters>> | undefined,
  defaultFilters: TFilters,
  initialSort: string,
  initialPage: number,
  search: string,
) {
  if (!urlAdapter) {
    return {
      debouncedSearchTerm: '',
      sortBy: initialSort,
      currentPage: initialPage,
      filters: defaultFilters,
    };
  }
  const partial = urlAdapter.hydrateFromSearch(search || '');
  return {
    debouncedSearchTerm: partial.q ?? '',
    sortBy: partial.sort ?? initialSort,
    currentPage: partial.page ?? initialPage,
    filters: partial.filters ?? defaultFilters,
  };
}

export function useFilteredListingControls<TFilters>({
  defaultFilters,
  isFiltersActive,
  urlAdapter,
  debounceMs = 300,
  initialSort = 'relevance',
  initialPage = 1,
  searchPlaceholder,
}: UseFilteredListingControlsOptions<TFilters>) {
  const location = useLocation();
  const [initial] = useState(() =>
    readControlsInitialState(
      urlAdapter,
      defaultFilters,
      initialSort,
      initialPage,
      location.search,
    ),
  );

  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(
    initial.debouncedSearchTerm,
  );
  const [currentPage, setCurrentPage] = useState(initial.currentPage);
  const [sortBy, setSortBy] = useState(initial.sortBy);
  const [filters, setFilters] = useState<TFilters>(initial.filters);

  const handleDebouncedSearchChange = useCallback((value: string) => {
    setDebouncedSearchTerm(value);
    setCurrentPage(1);
  }, []);

  const listingSnapshot = useMemo(
    () => ({
      q: debouncedSearchTerm,
      sort: sortBy,
      page: currentPage,
      filters,
    }),
    [debouncedSearchTerm, sortBy, currentPage, filters],
  );

  const onListingHydrateOrPop = useCallback(
    (partial: Partial<FilteredListingControlsSnapshot<TFilters>>) => {
      if (partial.q !== undefined) {
        setDebouncedSearchTerm(partial.q);
      }
      if (partial.sort !== undefined) setSortBy(partial.sort);
      if (partial.page !== undefined) setCurrentPage(partial.page);
      if (partial.filters !== undefined) setFilters(partial.filters);
    },
    [],
  );

  const handleClearAllFilters = useCallback(() => {
    setDebouncedSearchTerm('');
    setCurrentPage(1);
    setFilters(defaultFilters);
  }, [defaultFilters]);

  const handleFilterChange = useCallback(
    (updater: (prev: TFilters) => TFilters) => {
      setFilters(updater);
      setCurrentPage(1);
    },
    [],
  );

  const filtersActive = isFiltersActive(filters);
  const searchHasValue = debouncedSearchTerm.trim().length > 0;

  const search: FilteredListingSearchRowProps = useMemo(
    () => ({
      placeholder: searchPlaceholder,
      value: debouncedSearchTerm,
      onDebouncedChange: handleDebouncedSearchChange,
      debounceMs,
      allowClear: true,
    }),
    [
      searchPlaceholder,
      debouncedSearchTerm,
      handleDebouncedSearchChange,
      debounceMs,
    ],
  );

  return {
    search,
    filters,
    setFilters,
    handleFilterChange,
    handleClearAllFilters,
    filtersActive,
    searchHasValue,
    debouncedSearchTerm,
    sortBy,
    setSortBy,
    currentPage,
    setCurrentPage,
    listingSnapshot,
    onListingHydrateOrPop,
  };
}
